/**
 * Connectivity mode editor (#252) — per-modem "persistent attach" / "PLMN hunt".
 *
 * Saves through `edgeDeviceService.updateModemSettings(deviceId, imei,
 * { connectivityMode, connectivityConfig })`; central-server validates, stamps
 * the anchor SIM and pushes `connectivity_config_sync` to the Pi. Pass windows
 * (hunt mode) go through the api function's /devices/:id/pass-windows routes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import type { CentralEdgeModem, ConnectivityConfig, ConnectivityMode, TestConfiguration } from '@simcheck/shared-types';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
  Button, Input, Label, Switch, Badge, Textarea, DeleteConfirmDialog,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import { getTestConfigurations } from '@/services/testConfigurationService';
import { listPassWindows, uploadPassWindowsCsv, deletePassWindow, type PassWindowRow } from '@/services/connectivityService';
import { useToast } from '@/contexts/ToastContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { Trash2 } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deviceId: string;
  /** Org that owns the device — connectivity modes are private-edge only. */
  deviceOwnerOrgId: string | null | undefined;
  modem: CentralEdgeModem | null;
  /** Mode the user picked in the table (or the modem's current mode). */
  initialMode: ConnectivityMode;
  onSaved?: () => void;
}

type Draft = {
  apn: string;
  probeIntervalSec: number;
  uploadMode: 'events' | 'continuous';
  windowMinutes: number;
  pcap: boolean;
  diagEnabled: boolean;
  diagMask: 'signaling' | 'full' | 'deep';
  deepLogMode: '' | 'scat' | 'qmdl';
  retentionDays: number;
  anchorPolicy: 'follow' | 'strict';
  onAttachTestConfigId: string;
  onAttachDebounceSec: number;
  onAttachMaxPerHour: number;
  // hunt
  targetPlmns: string;
  lteBands: string;
  nrBands: string;
  scanMode: 'auto' | 'scheduled';
  scanIntervalSec: number;
  scanMethod: 'qscan' | 'cops';
  attachPolicy: 'on_detect' | 'always_in_window';
  attachTimeoutSec: number;
  holdSec: number;
  idlePolicy: 'detached' | 'terrestrial_auto';
  // rf
  rfEnabled: boolean;
  unitAntennaGainDbi: number;
  cableLossDb: number;
  refUeModel: string;
  refUeGainDbi: number;
};

function draftFrom(cfg: Partial<ConnectivityConfig> | undefined | null, modem: CentralEdgeModem | null): Draft {
  const c: any = cfg || {};
  const h: any = c.hunt || {};
  const r: any = c.rfOffset;
  return {
    apn: c.apn || '',
    probeIntervalSec: c.probe?.intervalSec ?? 60,
    uploadMode: c.capture?.uploadMode ?? 'events',
    windowMinutes: c.capture?.windowMinutes ?? 10,
    pcap: c.capture?.pcap !== false,
    diagEnabled: c.capture?.diagEnabled ?? (modem?.diagCaptureEnabled !== false),
    diagMask: c.capture?.diagMask ?? 'signaling',
    deepLogMode: c.capture?.deepLogMode ?? '',
    retentionDays: c.retentionDays ?? 90,
    anchorPolicy: c.anchorPolicy ?? 'follow',
    onAttachTestConfigId: c.onAttachTestConfigId || '',
    onAttachDebounceSec: c.onAttachDebounceSec ?? 300,
    onAttachMaxPerHour: c.onAttachMaxPerHour ?? 4,
    targetPlmns: (h.targetPlmns || []).join(', '),
    lteBands: (h.bands?.lte || []).join(', '),
    nrBands: (h.bands?.nr || []).join(', '),
    scanMode: h.scanMode ?? 'auto',
    scanIntervalSec: h.scanIntervalSec ?? 60,
    scanMethod: h.scanMethod ?? 'qscan',
    attachPolicy: h.attachPolicy ?? 'on_detect',
    attachTimeoutSec: h.attachTimeoutSec ?? 120,
    holdSec: h.holdSec ?? 300,
    idlePolicy: h.idlePolicy ?? 'detached',
    rfEnabled: !!r,
    unitAntennaGainDbi: r?.unitAntennaGainDbi ?? 0,
    cableLossDb: r?.cableLossDb ?? 0,
    refUeModel: r?.refUeModel ?? '',
    refUeGainDbi: r?.refUeGainDbi ?? 0,
  };
}

const NONE = '__none__';

/** A test config may auto-run on attach only if it can't disturb the anchor. */
function isNonDisruptive(tc: TestConfiguration): boolean {
  const t: any = tc;
  if (t.forcedRat && t.forcedRat !== 'auto') return false;
  if (t.forcedNetworkMccMnc) return false;
  if (t.esim?.mode === 'batch') return false;
  return true;
}

const parseBands = (s: string): number[] =>
  s.split(/[\s,;]+/).map((x) => parseInt(x.replace(/^n/i, ''), 10)).filter((n) => Number.isInteger(n) && n > 0);

export const ConnectivityModeSheet: React.FC<Props> = ({ open, onOpenChange, deviceId, deviceOwnerOrgId, modem, initialMode, onSaved }) => {
  const { success, error: showError } = useToast();
  const fmt = useDateFormat();
  const [mode, setMode] = useState<ConnectivityMode>(initialMode);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(modem?.connectivityConfig, modem));
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState<TestConfiguration[]>([]);
  const [windows, setWindows] = useState<PassWindowRow[]>([]);
  // Pass window waiting for confirmation in the DeleteConfirmDialog
  const [pendingWindow, setPendingWindow] = useState<PassWindowRow | null>(null);
  const [csv, setCsv] = useState('');
  const [uploading, setUploading] = useState(false);

  const imei = modem?.imei || modem?.modemId || '';
  const anchor = modem?.connectivityConfig?.anchorIccid || modem?.simInfo?.activeIccid || modem?.simInfo?.iccid || null;

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setDraft(draftFrom(modem?.connectivityConfig, modem));
  }, [open, initialMode, modem]);

  useEffect(() => {
    if (!open || !deviceOwnerOrgId) return;
    getTestConfigurations(deviceOwnerOrgId).then((list) => setConfigs(list.filter(isNonDisruptive))).catch(() => setConfigs([]));
  }, [open, deviceOwnerOrgId]);

  const reloadWindows = React.useCallback(() => {
    if (!open || mode !== 'hunt') return;
    listPassWindows(deviceId, { from: new Date(Date.now() - 86400_000), to: new Date(Date.now() + 30 * 86400_000), limit: 500 })
      .then(setWindows)
      .catch(() => setWindows([]));
  }, [open, mode, deviceId]);
  useEffect(() => { reloadWindows(); }, [reloadWindows]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const num = (v: string, dflt: number) => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : dflt; };

  const disabledReason = useMemo(() => {
    if (!deviceOwnerOrgId) return 'Connectivity modes are available on private (org-owned) edges only.';
    if (modem?.backhaul) return 'This modem is the device backhaul.';
    return null;
  }, [deviceOwnerOrgId, modem]);

  const handleSave = async () => {
    if (!imei) return;
    const apn = draft.apn.trim();
    if (!apn) { showError('APN is required'); return; }
    const connectivityConfig: any = {
      apn,
      probe: { intervalSec: draft.probeIntervalSec },
      capture: {
        uploadMode: draft.uploadMode,
        windowMinutes: draft.windowMinutes,
        pcap: draft.pcap,
        diagEnabled: draft.diagEnabled,
        diagMask: draft.diagMask,
        ...(draft.deepLogMode ? { deepLogMode: draft.deepLogMode } : {}),
      },
      onAttachTestConfigId: draft.onAttachTestConfigId || null,
      onAttachDebounceSec: draft.onAttachDebounceSec,
      onAttachMaxPerHour: draft.onAttachMaxPerHour,
      retentionDays: draft.retentionDays,
      anchorPolicy: draft.anchorPolicy,
      rfOffset: draft.rfEnabled
        ? { unitAntennaGainDbi: draft.unitAntennaGainDbi, cableLossDb: draft.cableLossDb, refUeModel: draft.refUeModel, refUeGainDbi: draft.refUeGainDbi }
        : null,
    };
    if (mode === 'hunt') {
      const plmns = draft.targetPlmns.split(/[\s,;]+/).map((x) => x.replace(/\D/g, '')).filter(Boolean);
      if (plmns.length === 0) { showError('Hunt mode needs at least one target PLMN (MCCMNC)'); return; }
      connectivityConfig.hunt = {
        targetPlmns: plmns,
        bands: {
          lte: parseBands(draft.lteBands),
          ...(parseBands(draft.nrBands).length ? { nr: parseBands(draft.nrBands) } : {}),
        },
        scanMode: draft.scanMode,
        scanIntervalSec: draft.scanIntervalSec,
        scanMethod: draft.scanMethod,
        attachPolicy: draft.attachPolicy,
        attachTimeoutSec: draft.attachTimeoutSec,
        holdSec: draft.holdSec,
        idlePolicy: draft.idlePolicy,
      };
    }
    setSaving(true);
    try {
      await edgeDeviceService.updateModemSettings(deviceId, imei, { connectivityMode: mode, connectivityConfig });
      success(mode === 'hunt' ? 'PLMN hunt mode saved' : 'Persistent attach mode saved');
      onSaved?.();
      onOpenChange(false);
    } catch (err: any) {
      showError(err?.message || 'Failed to save connectivity mode');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadCsv = async () => {
    if (!csv.trim()) return;
    setUploading(true);
    try {
      const r = await uploadPassWindowsCsv(deviceId, csv);
      success(`${r.created} window(s) added${r.skipped ? `, ${r.skipped} duplicate(s) skipped` : ''}`);
      setCsv('');
      reloadWindows();
    } catch (err: any) {
      showError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const field = (label: string, control: React.ReactNode, hint?: string) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {control}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Connectivity mode — {modem?.modemModelName || modem?.model || 'modem'} …{imei.slice(-6)}</SheetTitle>
          <SheetDescription>
            {mode === 'hunt'
              ? 'Scan for target PLMNs on a schedule or inside pass windows, force-attach when seen, record every attempt.'
              : 'Keep the modem attached to its anchor SIM with a warm bearer; tests reuse the connection.'}
            {anchor && <> Anchor SIM: <span className="font-mono">{anchor}</span>.</>}
          </SheetDescription>
        </SheetHeader>

        {disabledReason && (
          <p className="mt-3 rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">{disabledReason}</p>
        )}

        <div className="mt-4 space-y-5">
          {field('Mode', (
            <Select value={mode} onValueChange={(v) => setMode(v as ConnectivityMode)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="persistent">Persistent attach</SelectItem>
                <SelectItem value="hunt">PLMN hunt (satellite / targeted)</SelectItem>
              </SelectContent>
            </Select>
          ))}

          <section className="space-y-3">
            <h4 className="text-sm font-medium">Connection</h4>
            {field('APN', <Input value={draft.apn} onChange={(e) => set('apn', e.target.value)} placeholder="internet" />, 'Tests on this modem always use this APN.')}
            <div className="grid grid-cols-2 gap-3">
              {field('Probe interval (s)', <Input type="number" min={15} value={draft.probeIntervalSec} onChange={(e) => set('probeIntervalSec', num(e.target.value, 60))} />, 'ping / DNS / HTTPS inside the modem namespace')}
              {field('Retention (days)', <Input type="number" min={7} max={730} value={draft.retentionDays} onChange={(e) => set('retentionDays', num(e.target.value, 90))} />, 'events, probes and captures')}
            </div>
            {field('If the SIM identity changes', (
              <Select value={draft.anchorPolicy} onValueChange={(v) => set('anchorPolicy', v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="follow">Follow the live SIM (multi-IMSI / eUICC fallback rotation)</SelectItem>
                  <SelectItem value="strict">Strict — stop and wait for the anchor profile</SelectItem>
                </SelectContent>
              </Select>
            ), 'eSIM switches are refused on a managed modem, so a changed ICCID is the SIM rotating on its own. Follow records anchor_rotated and stays attached; strict parks the modem in anchor_mismatch.')}
          </section>

          <section className="space-y-3">
            <h4 className="text-sm font-medium">Capture</h4>
            <div className="grid grid-cols-2 gap-3">
              {field('Upload', (
                <Select value={draft.uploadMode} onValueChange={(v) => set('uploadMode', v as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="events">On events (detach, attach attempt, probe failure)</SelectItem>
                    <SelectItem value="continuous">Continuous (every chunk)</SelectItem>
                  </SelectContent>
                </Select>
              ))}
              {field('Window before event (min)', <Input type="number" min={1} max={60} value={draft.windowMinutes} onChange={(e) => set('windowMinutes', num(e.target.value, 10))} />)}
            </div>
            <div className="flex flex-wrap items-center gap-5">
              <label className="flex items-center gap-2 text-xs"><Switch checked={draft.pcap} onCheckedChange={(v) => set('pcap', v)} />pcap ring</label>
              <label className="flex items-center gap-2 text-xs"><Switch checked={draft.diagEnabled} onCheckedChange={(v) => set('diagEnabled', v)} />DIAG (L3 trace)</label>
              <Select value={draft.diagMask} onValueChange={(v) => set('diagMask', v as any)}>
                <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="signaling">signaling</SelectItem>
                  <SelectItem value="full">full</SelectItem>
                  <SelectItem value="deep">deep (L1/L2, QCAT)</SelectItem>
                </SelectContent>
              </Select>
              <Select value={draft.deepLogMode || NONE} onValueChange={(v) => set('deepLogMode', (v === NONE ? '' : v) as any)}>
                <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="deep log" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Deep log: default</SelectItem>
                  <SelectItem value="scat">signalcat (pcap)</SelectItem>
                  <SelectItem value="qmdl">raw .qmdl (QCAT)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-sm font-medium">Run a test on attach</h4>
            {field('Test configuration', (
              <Select value={draft.onAttachTestConfigId || NONE} onValueChange={(v) => set('onAttachTestConfigId', v === NONE ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {configs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ), 'Only configurations without a forced network type / PLMN / eSIM group are listed.')}
            <div className="grid grid-cols-2 gap-3">
              {field('Debounce (s)', <Input type="number" min={0} value={draft.onAttachDebounceSec} onChange={(e) => set('onAttachDebounceSec', num(e.target.value, 300))} />)}
              {field('Max runs / hour', <Input type="number" min={1} max={60} value={draft.onAttachMaxPerHour} onChange={(e) => set('onAttachMaxPerHour', num(e.target.value, 4))} />)}
            </div>
          </section>

          {mode === 'hunt' && (
            <section className="space-y-3">
              <h4 className="text-sm font-medium">PLMN hunt</h4>
              {field('Target PLMNs (MCCMNC)', <Input value={draft.targetPlmns} onChange={(e) => set('targetPlmns', e.target.value)} placeholder="313100, 310410" />)}
              {field('LTE bands', <Input value={draft.lteBands} onChange={(e) => set('lteBands', e.target.value)} placeholder="5, 8, 12, 14, 26" />, 'Band lock applied before each scan (RM520N: AT+QNWPREFCFG="lte_band").')}
              {field('NR bands (5G)', <Input value={draft.nrBands} onChange={(e) => set('nrBands', e.target.value)} placeholder="Leave empty to scan LTE only" />, 'Set to scan and lock 5G NR too (RM520N: SA and NSA band masks; the scan then covers LTE and NR).')}
              <div className="grid grid-cols-2 gap-3">
                {field('Scan mode', (
                  <Select value={draft.scanMode} onValueChange={(v) => set('scanMode', v as any)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto — fixed interval</SelectItem>
                      <SelectItem value="scheduled">Scheduled — inside pass windows</SelectItem>
                    </SelectContent>
                  </Select>
                ))}
                {field('Scan interval (s)', <Input type="number" min={20} value={draft.scanIntervalSec} onChange={(e) => set('scanIntervalSec', num(e.target.value, 60))} />)}
                {field('Scan method', (
                  <Select value={draft.scanMethod} onValueChange={(v) => set('scanMethod', v as any)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="qscan">AT+QSCAN (cell list, no attach)</SelectItem>
                      <SelectItem value="cops">AT+COPS=? (fallback)</SelectItem>
                    </SelectContent>
                  </Select>
                ))}
                {field('Attach policy', (
                  <Select value={draft.attachPolicy} onValueChange={(v) => set('attachPolicy', v as any)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="on_detect">When the target is seen</SelectItem>
                      <SelectItem value="always_in_window">Always, inside a window</SelectItem>
                    </SelectContent>
                  </Select>
                ))}
                {field('Attach timeout (s)', <Input type="number" min={30} max={900} value={draft.attachTimeoutSec} onChange={(e) => set('attachTimeoutSec', num(e.target.value, 120))} />)}
                {field('Hold after attach (s)', <Input type="number" min={30} value={draft.holdSec} onChange={(e) => set('holdSec', num(e.target.value, 300))} />)}
                {field('Between windows', (
                  <Select value={draft.idlePolicy} onValueChange={(v) => set('idlePolicy', v as any)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="detached">Stay detached</SelectItem>
                      <SelectItem value="terrestrial_auto">Auto-attach terrestrial (reference tests)</SelectItem>
                    </SelectContent>
                  </Select>
                ))}
              </div>

              <div className="space-y-2 rounded border bg-muted/30 p-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Pass windows (next 30 days)</Label>
                  <Badge variant="outline">{windows.length}</Badge>
                </div>
                {windows.length > 0 && (
                  <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
                    {windows.map((w) => (
                      <li key={w.id} className="flex items-center justify-between gap-2">
                        <span className="font-mono">{fmt.formatDateTime(w.start)} → {fmt.formatTime(w.end)}{w.satelliteId ? ` · ${w.satelliteId}` : ''}</span>
                        <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => setPendingWindow(w)} aria-label="Delete window">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
                <Textarea
                  value={csv}
                  onChange={(e) => setCsv(e.target.value)}
                  placeholder={'start,end,satelliteId\n2026-10-01T03:12:00Z,2026-10-01T03:21:00Z,SAT-1'}
                  rows={4}
                  className="font-mono text-xs"
                />
                <Button variant="outline" size="sm" onClick={handleUploadCsv} disabled={uploading || !csv.trim()}>
                  {uploading ? 'Uploading…' : 'Add windows'}
                </Button>
              </div>
            </section>
          )}

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Gain normalisation (RSRP)</h4>
              <Switch checked={draft.rfEnabled} onCheckedChange={(v) => set('rfEnabled', v)} />
            </div>
            {draft.rfEnabled && (
              <div className="grid grid-cols-2 gap-3">
                {field('Unit antenna gain (dBi)', <Input type="number" step="0.1" value={draft.unitAntennaGainDbi} onChange={(e) => set('unitAntennaGainDbi', parseFloat(e.target.value) || 0)} />)}
                {field('Cable + connector loss (dB)', <Input type="number" step="0.1" value={draft.cableLossDb} onChange={(e) => set('cableLossDb', parseFloat(e.target.value) || 0)} />)}
                {field('Reference UE model', <Input value={draft.refUeModel} onChange={(e) => set('refUeModel', e.target.value)} />)}
                {field('Reference UE gain (dBi)', <Input type="number" step="0.1" value={draft.refUeGainDbi} onChange={(e) => set('refUeGainDbi', parseFloat(e.target.value) || 0)} />)}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">normalised = measured − unit gain + reference gain + cable loss; raw and normalised are reported side by side.</p>
          </section>
        </div>

        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !!disabledReason || !imei}>{saving ? 'Saving…' : 'Save'}</Button>
        </SheetFooter>

        <DeleteConfirmDialog
          open={pendingWindow !== null}
          onClose={() => setPendingWindow(null)}
          itemName={pendingWindow ? `Pass window ${fmt.formatDateTime(pendingWindow.start)} to ${fmt.formatTime(pendingWindow.end)}${pendingWindow.satelliteId ? ` (${pendingWindow.satelliteId})` : ''}` : undefined}
          onConfirm={async () => {
            if (!pendingWindow) return;
            await deletePassWindow(deviceId, pendingWindow.id).then(reloadWindows).catch((e) => showError(e?.message || 'Delete failed'));
          }}
        />
      </SheetContent>
    </Sheet>
  );
};
